
import { Color, Label, LabelType } from '@/model';
import labels, { getLabelTypeOrder } from '@/store/labels';
import ColorPicker from '@/components/ColorPicker.vue';
import Vue from 'vue';

interface EditableLabel {
  id?: number;
  type: string;
  code: string;
  color: Color;
  description?: string;
  externalId?: string;
  selectable: boolean;
}

const defaultColor: Color = { r: 158, g: 158, b: 158 };

function createEditLabel(label: Label | undefined): EditableLabel {
  if (label) {
    return {
      id: label.id,
      type: label.type,
      code: label.code,
      color: label.color,
      description: label.description,
      externalId: label.externalId,
      selectable: label.selectable,
    };
  }
  return {
    type: '',
    code: '',
    color: defaultColor,
    selectable: true,
  };
}

export default Vue.extend({
  name: 'LabelEditorDialog',

  components: {
    ColorPicker,
  },

  props: {
    value: { type: Boolean, default: false },
    label: { type: Object as () => Label | undefined },
  },

  data() {
    return {
      saving: false,
      editLabel: createEditLabel(this.label),
      rules: {
        required: (v: string) => !!v || 'Required',
      },
    };
  },

  computed: {
    isNew(): boolean {
      return !this.label?.id;
    },
    labelTypeOptions(): string[] {
      return getLabelTypeOrder();
    },
    previewColor(): string {
      const c = this.editLabel.color || defaultColor;
      return `rgba(${c.r}, ${c.g}, ${c.b}, 1)`;
    },
    isValid(): boolean {
      return !!this.editLabel.type && !!this.editLabel.code;
    },
    isNewType(): boolean {
      return !!this.editLabel.type && !labels.dispatch.getLabelTypeByCode(this.editLabel.type);
    },
  },

  watch: {
    label: {
      handler(v: Label | undefined) {
        this.editLabel = createEditLabel(v);
      },
      immediate: true,
    },
    value(v: boolean) {
      if (v) {
        this.editLabel = createEditLabel(this.label);
      }
    },

  },

  methods: {
    close() {
      this.$emit('input', false);
    },
    updateColorOnTypeChange(newType: string) {
      const existingType = labels.dispatch.getLabelTypeByCode(newType);
      if (existingType) {
        this.editLabel.color = existingType.color;
      }
    },
    async save() {
      this.saving = true;
      try {
        const existingType = labels.dispatch.getLabelTypeByCode(this.editLabel.type);
        if (!existingType) {
          const maxSortOrder = labels.state.labelTypes
            .map((lt) => lt.sortOrder ?? 0)
            .reduce((max, so) => Math.max(max, so), 0);
          const newType: LabelType = {
            code: this.editLabel.type,
            color: this.editLabel.color,
            selectable: true,
            sortOrder: maxSortOrder + 10,
          };
          await labels.dispatch.saveLabelType(newType);
        }

        const labelToSave = {
          id: this.editLabel.id || 0,
          type: this.editLabel.type,
          code: this.editLabel.code,
          color: this.editLabel.color,
          description: this.editLabel.description,
          externalId: this.editLabel.externalId,
          selectable: this.editLabel.selectable,
        } as Label;
        const saved = await labels.dispatch.save(labelToSave);
        this.$emit('saved', saved);
        this.close();
      } finally {
        this.saving = false;
      }
    },
  },
});
